import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Typography, 
    Container, 
    IconButton,
    Paper,
    Tabs,
    Tab,
    useTheme,
    CircularProgress,
    Chip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';
import { sandboxConsentsService, SandboxConsentDetails as ConsentDetailsType } from '../../services/sandbox-consents-service';
import { languageService, Language } from '../../services/language-service';
import DetailsTab from './components/DetailsTab';
import CollectionPointTab from './components/CollectionPointTab';
import GeolocationTabV2 from './components/GeolocationTabV2';
import PrivacyPolicyTab from './components/PrivacyPolicyTab';
import YourRightsTab from './components/YourRightsTab';
import ContactDpoTab from './components/ContactDpoTab';
import DataElementsTab from './components/DataElementsTab';
import PurposesTab from './components/PurposesTab';
import PreferencesTab from './components/PreferencesTab';

const SandboxConsentDetails: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { consentId } = useParams<{ consentId: string }>();

    // State management
    const [consentData, setConsentData] = useState<ConsentDetailsType | null>(null);
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [languages, setLanguages] = useState<Language[]>([]);

    // Tab configuration
    const tabs = [
        { label: 'Details', value: 0 },
        { label: 'Collection Point', value: 1 },
        { label: 'Geolocation', value: 2 },
        { label: 'Privacy Policy', value: 3 },
        { label: 'Your Rights', value: 4 },
        { label: 'Contact DPO', value: 5 },
        { label: 'Data Elements', value: 6 },
        { label: 'Purposes', value: 7 },
        { label: 'Preferences', value: 8 }
    ];

    // Load languages on component mount
    useEffect(() => {
        const loadLanguages = async () => {
            try {
                const languageData = await languageService.getSupportedLanguages();
                setLanguages(languageData);
            } catch (error) {
                console.error('Failed to load languages:', error);
            }
        };
        loadLanguages();
    }, []);

    // Fetch consent data
    useEffect(() => {
        const fetchConsentData = async () => {
            if (!consentId) return;
            
            try {
                setLoading(true);
                setError(null);
                const response = await sandboxConsentsService.getSandboxConsentById(parseInt(consentId));
                if (response.success) {
                    setConsentData(response.data);
                } else {
                    setError('Failed to fetch consent details');
                }
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to fetch consent details');
            } finally {
                setLoading(false);
            }
        };

        fetchConsentData();
    }, [consentId]);

    // Get language name from code
    const getLanguageName = (languageCode: string): string => {
        // Hardcode English for 'en' as requested
        if (languageCode.toLowerCase() === 'en') {
            return 'English';
        }
        
        const language = languages.find(lang => 
            lang.code.toLowerCase() === languageCode.toLowerCase()
        );
        
        if (language) {
            if (language.name !== language.localeName) {
                return `${language.name} (${language.localeName})`;
            }
            return language.name;
        }
        
        // Fallback to uppercase code if not found
        return languageCode.toUpperCase();
    };

    // Get Data Principal display value based on sandbox logic
    const getDataPrincipalDisplayValue = (): string => {
        if (!consentData) return '';
        
        const { identifierValue, vaultId } = consentData.details.dataPrincipal;
        
        // For sandbox records, use identifierValue
        if (identifierValue === null || identifierValue === undefined || identifierValue === '') {
            // If identifierValue is null/empty
            if (vaultId === null || vaultId === undefined) {
                return 'Unknown';
            } else {
                return 'Hidden';
            }
        }
        
        return identifierValue;
    };

    const handleBackClick = () => {
        navigate('/sandbox/consents');
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    return (
        <Container maxWidth="xl">
            <Box sx={{ py: 3 }}>
                {/* Header with Back Button */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <IconButton
                        onClick={handleBackClick}
                        sx={{ 
                            mr: 2, 
                            color: 'primary.main',
                            '&:hover': {
                                bgcolor: 'primary.light',
                                color: 'primary.contrastText'
                            }
                        }}
                    >
                        <ArrowBackIcon sx={{ fontSize: 24 }} />
                    </IconButton>
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'text.primary' }}>
                            Consent Details
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Consent ID: #{consentId}
                        </Typography>
                    </Box>
                    {consentData && (
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <Chip 
                                label={getDataPrincipalDisplayValue()}
                                color="primary"
                                variant="outlined"
                            />
                            <Chip 
                                label={getLanguageName(consentData.details.consentHeader.languageCode)}
                                color="secondary"
                                variant="outlined"
                            />
                        </Box>
                    )}
                </Box>

                {/* Summary Information */}
                {consentData && (
                    <Paper sx={{ p: 3, mb: 3, bgcolor: 'background.paper' }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Data Principal</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {getDataPrincipalDisplayValue()}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Consent Time</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {new Date(consentData.details.consentHeader.consentTimestamp).toLocaleString()}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Collection Point</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {consentData.collectionPoint.name}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Client</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {consentData.details.client.clientName}
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                )}

                {/* Loading State */}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                        <Box sx={{ textAlign: 'center' }}>
                            <CircularProgress size={40} sx={{ mb: 2 }} />
                            <Typography variant="body2" color="text.secondary">
                                Loading consent details...
                            </Typography>
                        </Box>
                    </Box>
                )}

                {/* Error State */}
                {error && (
                    <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'error.light' }}>
                        <Typography variant="h6" color="error" gutterBottom>
                            Error Loading Consent Details
                        </Typography>
                        <Typography variant="body1" color="error">
                            {error}
                        </Typography>
                    </Paper>
                )}

                {/* Tab Navigation and Content */}
                {consentData && (
                    <Paper sx={{ bgcolor: 'background.paper' }}>
                        <Tabs 
                            value={activeTab} 
                            onChange={handleTabChange}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{ 
                                borderBottom: 1, 
                                borderColor: 'divider',
                                px: 2
                            }}
                        >
                            {tabs.map((tab) => (
                                <Tab 
                                    key={tab.value} 
                                    label={tab.label} 
                                    value={tab.value}
                                    sx={{ minWidth: 'auto' }}
                                />
                            ))}
                        </Tabs>

                        {/* Tab Content */}
                        <Box sx={{ p: 3 }}>
                            {activeTab === 0 && (
                                <DetailsTab consentData={consentData} />
                            )}
                            {activeTab === 1 && (
                                <CollectionPointTab consentData={consentData} />
                            )}
                            {activeTab === 2 && (
                                <GeolocationTabV2 consentData={consentData} />
                            )}
                            {activeTab === 3 && (
                                <PrivacyPolicyTab consentData={consentData} />
                            )}
                            {activeTab === 4 && (
                                <YourRightsTab consentData={consentData} />
                            )}
                            {activeTab === 5 && (
                                <ContactDpoTab consentData={consentData} />
                            )}
                            {activeTab === 6 && (
                                <DataElementsTab consentData={consentData} />
                            )}
                            {activeTab === 7 && (
                                <PurposesTab consentData={consentData} />
                            )}
                            {activeTab === 8 && (
                                <PreferencesTab consentData={consentData} />
                            )}
                        </Box>
                    </Paper>
                )}
            </Box>
        </Container>
    );
};

export default SandboxConsentDetails;
